// Basic helpers
export type UUID = string;
export type ID = string; // ext ids like "105642", "785786"
export type ISODateString = `${number}-${number}-${number}`; // "YYYY-MM-DD"
export type ISODateTimeString = `${string}Z`; // "...Z"

export type PacingStatus = "behind" | "ahead" | "on_track" | "not_set" | "update";

// --- Nested refs found on each line item ---
export interface AccountRef {
  id: UUID;
  created_at: ISODateTimeString;
  updated_at: ISODateTimeString;
  status: string | null;
  ext_account_id: ID;
  title: string;
  source: string | null; // e.g., "yahoo_dsp"
  attention_needed: boolean;
  company: UUID | null; // nullable in other examples
}

export interface CampaignRef {
  id: UUID;
  account: AccountRef; // repeated block
  created_at: ISODateTimeString;
  updated_at: ISODateTimeString;
  status: string | null;
  ext_campaign_id: ID;
  title: string;
  platform: string | null; // e.g., "yahoo_dsp"
  channel: string | null;
  do_not_bill: boolean;
  attention_needed: boolean;
  sku: UUID | null;
  company: UUID | null;
}

// --- Leaf: line item within a campaign ---
export interface LineItem {
  id: UUID;
  account: AccountRef;
  campaign: CampaignRef;

  created_at: ISODateTimeString;
  updated_at: ISODateTimeString;
  status: string; // e.g., "active"
  external_item_id: string;
  line_item_id: string;
  line_item_name: string;

  impressions: number;
  budget: number; // source provides as string (e.g., "400.00")
  daily_budget: number; // string in sample
  spend: number; // string in sample
  percent_complete: number; // string in sample
  pacing_item_status: PacingStatus;

  start_date: ISODateString;
  end_date: ISODateString;
  days_remaining: number; // may be negative
  is_visible: boolean;
  is_locked: boolean;
  notes: string | null;
  view: UUID;
}

// --- Rollup aggregate used at campaign & account levels ---
export interface Aggregate {
  budget: number;
  spend: number;
  daily_budget: number;
  impressions: number;
  start_date: ISODateString | null;
  end_date: ISODateString | null;
  percent_complete: number | null;
  status: PacingStatus;
  is_visible: boolean;
  is_locked: boolean;
}

// --- Campaign node in the hierarchy ---
export interface CampaignNode {
  id: ID;
  name: string;
  platform: string | null;
  attention_needed: boolean;
  line_items: LineItem[];
  agg: Aggregate;
}

// --- Account/Advertiser node at the top ---
export interface AdvertiserNode {
  id: ID;
  name: string;
  source: string | null;
  attention_needed: boolean;
  campaigns: CampaignNode[];
  agg: Aggregate;
}

export interface PacingDataApiResponse {
  items: AdvertiserNode[];
}

export type ViewType = 'table' | 'gantt';

export type UserFilterType = 'all' | 'on_track' | 'ahead' | 'behind' | 'paused' | 'update';

export const presetPacingRanges = [
  { key: 'current_week', label: 'Current week' },
  { key: 'next_week', label: 'Next week' },
  { key: 'current_month', label: 'Current month' },
  { key: 'next_month', label: 'Next month' },
  { key: 'current_quarter', label: 'Current quarter' },
  { key: 'current_year', label: 'Current year' },
];